import React, { useEffect, useState } from "react";
import { Trash2, Plus, Save, Webhook as WebhookIcon, Mail, Palette } from "lucide-react";
import api from "@/lib/api";
import { useAuth, formatApiError } from "@/lib/auth";
import { toast } from "sonner";

export default function Settings() {
  const { agency, refresh } = useAuth();
  const [branding, setBranding] = useState({
    logo_url: agency?.branding?.logo_url || "",
    primary_color: agency?.branding?.primary_color || "#0F172A",
    welcome_checklist: agency?.branding?.welcome_checklist || [],
  });
  const [checklistInput, setChecklistInput] = useState("");
  const [smtp, setSmtp] = useState({ host: "", port: 587, username: "", password: "", from_email: "", use_tls: true });
  const [hooks, setHooks] = useState([]);
  const [hookUrl, setHookUrl] = useState("");
  const [domain, setDomain] = useState(agency?.custom_domain || "");

  const loadHooks = async () => {
    const { data } = await api.get("/webhooks");
    setHooks(data);
  };

  useEffect(() => {
    loadHooks();
    if (agency) {
      setBranding({
        logo_url: agency.branding?.logo_url || "",
        primary_color: agency.branding?.primary_color || "#0F172A",
        welcome_checklist: agency.branding?.welcome_checklist || [],
      });
      setDomain(agency.custom_domain || "");
    }
  }, [agency]);

  const saveBranding = async (e) => {
    e.preventDefault();
    try {
      await api.patch("/agency/branding", branding);
      toast.success("Branding saved");
      await refresh();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const saveSmtp = async (e) => {
    e.preventDefault();
    try {
      await api.put("/agency/smtp", smtp);
      toast.success("SMTP credentials saved (encrypted at rest)");
      setSmtp({ ...smtp, password: "" });
      await refresh();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const addHook = async (e) => {
    e.preventDefault();
    await api.post("/webhooks", { url: hookUrl });
    setHookUrl("");
    await loadHooks();
    toast.success("Webhook added");
  };

  const delHook = async (id) => {
    await api.delete(`/webhooks/${id}`);
    await loadHooks();
  };

  const saveDomain = async () => {
    await api.post("/agency/custom-domain", { domain });
    toast.success("Custom domain saved. DNS verification coming soon.");
    await refresh();
  };

  const addChecklistItem = () => {
    if (!checklistInput.trim()) return;
    setBranding({ ...branding, welcome_checklist: [...branding.welcome_checklist, checklistInput.trim()] });
    setChecklistInput("");
  };

  const removeChecklistItem = (i) => {
    setBranding({ ...branding, welcome_checklist: branding.welcome_checklist.filter((_, idx) => idx !== i) });
  };

  return (
    <div className="p-8 md:p-12 animate-fade-up">
      <p className="eyebrow mb-2">Configuration</p>
      <h1 className="text-4xl font-bold tracking-tight text-slate-900 mb-8" data-testid="settings-title">Settings</h1>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <form onSubmit={saveBranding} className="bento p-8" data-testid="branding-card">
          <div className="flex items-center gap-3 mb-6">
            <Palette className="w-5 h-5" />
            <h2 className="text-2xl font-bold tracking-tight">White-label branding</h2>
          </div>

          <label className="text-sm font-medium text-slate-700 mb-1.5 block">Logo URL</label>
          <input placeholder="https://..." value={branding.logo_url} onChange={(e) => setBranding({ ...branding, logo_url: e.target.value })} className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 mb-4" data-testid="branding-logo-url" />

          <label className="text-sm font-medium text-slate-700 mb-1.5 block">Primary color</label>
          <div className="flex items-center gap-3 mb-4">
            <input type="color" value={branding.primary_color} onChange={(e) => setBranding({ ...branding, primary_color: e.target.value })} className="w-14 h-12 rounded-xl border border-slate-200 cursor-pointer" data-testid="branding-color-picker" />
            <input value={branding.primary_color} onChange={(e) => setBranding({ ...branding, primary_color: e.target.value })} className="flex-1 bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 font-mono text-sm" data-testid="branding-color-text" />
          </div>

          <label className="text-sm font-medium text-slate-700 mb-1.5 block">Welcome checklist</label>
          <div className="space-y-2 mb-2">
            {branding.welcome_checklist.map((item, i) => (
              <div key={`${item}-${i}`} className="flex items-center gap-2 bg-slate-50 rounded-xl px-3 py-2">
                <span className="flex-1 text-sm">{item}</span>
                <button type="button" onClick={() => removeChecklistItem(i)} className="text-slate-400 hover:text-red-500"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            ))}
          </div>
          <div className="flex gap-2 mb-6">
            <input value={checklistInput} onChange={(e) => setChecklistInput(e.target.value)} placeholder="Add item" className="flex-1 bg-slate-50 border border-slate-200 rounded-xl px-4 py-2 text-sm" data-testid="checklist-input" />
            <button type="button" onClick={addChecklistItem} className="bg-slate-100 hover:bg-slate-200 rounded-xl px-3" data-testid="checklist-add"><Plus className="w-4 h-4" /></button>
          </div>

          <button type="submit" className="bg-slate-900 text-white rounded-full px-5 py-2.5 font-medium hover:bg-slate-700 active:scale-95 inline-flex items-center gap-2" data-testid="save-branding-button"><Save className="w-4 h-4" /> Save branding</button>
        </form>

        <form onSubmit={saveSmtp} className="bento p-8" data-testid="smtp-card">
          <div className="flex items-center gap-3 mb-6">
            <Mail className="w-5 h-5" />
            <h2 className="text-2xl font-bold tracking-tight">Email (BYO-SMTP)</h2>
          </div>
          <p className="text-sm text-slate-500 mb-4">
            {agency?.smtp_configured ? "✓ Currently configured (encrypted at rest)" : "Not configured — emails log to console."}
          </p>
          <div className="grid grid-cols-2 gap-3 mb-3">
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1 block">Host</label>
              <input required={!!smtp.username} value={smtp.host} onChange={(e) => setSmtp({ ...smtp, host: e.target.value })} placeholder="smtp.gmail.com" className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-sm" data-testid="smtp-host" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1 block">Port</label>
              <input type="number" value={smtp.port} onChange={(e) => setSmtp({ ...smtp, port: parseInt(e.target.value) || 587 })} className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-sm" data-testid="smtp-port" />
            </div>
          </div>
          <label className="text-xs font-medium text-slate-600 mb-1 block">Username</label>
          <input value={smtp.username} onChange={(e) => setSmtp({ ...smtp, username: e.target.value })} className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-sm mb-3" data-testid="smtp-username" />
          <label className="text-xs font-medium text-slate-600 mb-1 block">Password</label>
          <input type="password" value={smtp.password} onChange={(e) => setSmtp({ ...smtp, password: e.target.value })} placeholder="••••••" className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-sm mb-3" data-testid="smtp-password" />
          <label className="text-xs font-medium text-slate-600 mb-1 block">From email</label>
          <input type="email" value={smtp.from_email} onChange={(e) => setSmtp({ ...smtp, from_email: e.target.value })} className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-sm mb-4" data-testid="smtp-from" />
          <button type="submit" disabled={!smtp.host || !smtp.username || !smtp.password} className="bg-slate-900 text-white rounded-full px-5 py-2.5 font-medium hover:bg-slate-700 active:scale-95 disabled:opacity-50" data-testid="save-smtp-button">Save SMTP</button>
        </form>

        <div className="bento p-8 lg:col-span-2" data-testid="webhooks-card">
          <div className="flex items-center gap-3 mb-6">
            <WebhookIcon className="w-5 h-5" />
            <h2 className="text-2xl font-bold tracking-tight">Outbound webhooks</h2>
          </div>
          <p className="text-sm text-slate-500 mb-4">
            Fires deterministic JSON payloads on <code className="font-mono text-xs">contract.signed</code>, <code className="font-mono text-xs">client.onboarded</code>, <code className="font-mono text-xs">referral.converted</code>.
          </p>
          <form onSubmit={addHook} className="flex gap-2 mb-4">
            <input required type="url" value={hookUrl} onChange={(e) => setHookUrl(e.target.value)} placeholder="https://hooks.zapier.com/..." className="flex-1 bg-slate-50 border border-slate-200 rounded-xl px-4 py-2 text-sm" data-testid="webhook-url-input" />
            <button type="submit" className="bg-slate-900 text-white rounded-full px-5 py-2 font-medium hover:bg-slate-700 active:scale-95 text-sm" data-testid="add-webhook-button">Add</button>
          </form>
          <div className="space-y-2">
            {hooks.length === 0 ? (
              <p className="text-sm text-slate-400">No webhooks configured.</p>
            ) : hooks.map((h) => (
              <div key={h.id} className="flex items-center gap-3 bg-slate-50 rounded-xl px-4 py-3" data-testid={`webhook-${h.id}`}>
                <code className="font-mono text-xs flex-1 truncate">{h.url}</code>
                <button onClick={() => delHook(h.id)} className="text-slate-400 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        </div>

        <div className="bento p-8 lg:col-span-2" data-testid="domain-card">
          <h2 className="text-2xl font-bold tracking-tight mb-4">Custom domain</h2>
          <p className="text-sm text-slate-500 mb-4">Serve your client portal on <code className="font-mono text-xs">portal.youragency.com</code>. DNS verification available in Phase 2.</p>
          <div className="flex gap-2">
            <input value={domain} onChange={(e) => setDomain(e.target.value)} placeholder="portal.youragency.com" className="flex-1 bg-slate-50 border border-slate-200 rounded-xl px-4 py-2 text-sm" data-testid="domain-input" />
            <button onClick={saveDomain} className="bg-slate-900 text-white rounded-full px-5 py-2 font-medium hover:bg-slate-700 active:scale-95 text-sm" data-testid="save-domain-button">Save</button>
          </div>
        </div>
      </div>
    </div>
  );
}
